import axios, { AxiosInstance, AxiosResponse } from 'axios';
import { Store } from 'vuex';
import { TrackJS } from 'trackjs';

export interface AxiosPluginConfig {
  token: string;
  apiVersion: string;
  baseURL: string;
  locale: string;
}

const defaultConfig = (): AxiosPluginConfig => ({
  token: '',
  apiVersion: '',
  baseURL: '',
  locale: '',
});

export default (config: () => AxiosPluginConfig = defaultConfig) => (store: Store<any>): void => {
  store.$axios = axios.create();
  store.$axios.defaults.headers.Authorization = true;
  store.$axios.defaults.withCredentials = true;

  // Request helpers ($get, $post, ...)
  for (const method of ['request', 'delete', 'get', 'head', 'options', 'post', 'put', 'patch']) {
    (store.$axios as any)['$' + method] = function() {
      const payload = arguments[1]; // Save reference to payload

      return this[method].apply(this, arguments).then((response: AxiosResponse<any>) => {
        if (response.status !== 200) {
          // @ts-ignore
          TrackJS?.addMetadata('Response', response);
          TrackJS?.addMetadata('Payload', JSON.stringify(payload));
        }

        if (response.data) {
          if (response.data.data instanceof Array && (response.data.links || response.data.meta)) {
            return response.data;
          }

          return response.data.data || response.data;
        }

        return response;
      });
    };
  }

  store.$axios.interceptors.request.use(request => {
    const { token, apiVersion, baseURL, locale } = config();

    request.baseURL = request.baseURL ?? baseURL;

    // `true` (the default) means "use the store token"; `false` skips auth
    // entirely; an explicit string (e.g. an Azure AD token) is left untouched.
    if (request.headers.Authorization === true) {
      request.headers.Authorization = `Bearer ${token}`;
    }

    if (apiVersion) {
      request.headers['x-api-version'] = apiVersion;
    }

    if (locale) {
      request.headers['x-api-locale'] = locale;
    }

    // Do something before request is sent
    return request;
  });
};

declare module 'axios' {
  interface AxiosInstance {
    $request<T = any>(config: AxiosRequestConfig): Promise<T>;
    $get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T>;
    $delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T>;
    $head<T = any>(url: string, config?: AxiosRequestConfig): Promise<T>;
    $options<T = any>(url: string, config?: AxiosRequestConfig): Promise<T>;
    $post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
    $put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
    $patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T>;
  }
}

declare module 'vuex/types' {
  interface Store<S> {
    $axios: AxiosInstance;
  }
}
